import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import type { BuyerProfile, ProfileMember } from '../lib/types';
import { AUTH_ENABLED } from '../lib/auth';

// Stub getToken for non-Clerk mode
const noopGetToken = async () => null;

export function useProfiles(getToken: () => Promise<string | null> = noopGetToken) {
  const [profiles, setProfiles] = useState<BuyerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    if (!AUTH_ENABLED) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<BuyerProfile[]>('/profiles', {}, getToken);
      setProfiles(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const createProfile = useCallback(async (body: {
    name: string;
    cashOnHand: number;
    grossAnnualIncome: number;
    monthlyDebts: number;
    creditScoreRange: string;
    userEmail?: string;
    userName?: string;
    [key: string]: unknown; // allow ProfileFormData extras (401k, coBorrower)
  }): Promise<BuyerProfile> => {
    const profile = await apiFetch<BuyerProfile>('/profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }, getToken);
    setProfiles(prev => [...prev, profile]);
    return profile;
  }, [getToken]);

  const updateProfile = useCallback(async (id: string, body: Partial<BuyerProfile>): Promise<BuyerProfile> => {
    const profile = await apiFetch<BuyerProfile>(`/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, getToken);
    setProfiles(prev => prev.map(p => p.id === id ? profile : p));
    return profile;
  }, [getToken]);

  const deleteProfile = useCallback(async (id: string) => {
    await apiFetch(`/profiles/${id}`, { method: 'DELETE' }, getToken);
    setProfiles(prev => prev.filter(p => p.id !== id));
  }, [getToken]);

  return { profiles, loading, error, fetchProfiles, createProfile, updateProfile, deleteProfile };
}

export function useProfileDetail(profileId: string | undefined, getToken: () => Promise<string | null> = noopGetToken) {
  const [profile, setProfile] = useState<(BuyerProfile & { members?: ProfileMember[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!AUTH_ENABLED || !profileId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<BuyerProfile & { members?: ProfileMember[] }>(`/profiles/${profileId}`, {}, getToken);
      setProfile(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileId, getToken]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateProfile = useCallback(async (body: Partial<BuyerProfile>): Promise<BuyerProfile> => {
    if (!profileId) throw new Error('No profile id');
    const updated = await apiFetch<BuyerProfile>(`/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, getToken);
    setProfile(prev => prev ? { ...prev, ...updated } : updated);
    return updated;
  }, [profileId, getToken]);

  return { profile, loading, error, refetch: fetchProfile, updateProfile };
}
